import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMarketContext } from '../../contexts/MarketContext';
import { Broadcast, ChartBar, Timer } from '@phosphor-icons/react';

export function StatusBar() {
  const { t } = useTranslation();
  const { markets } = useMarketContext();
  const [latency, setLatency] = useState(0);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });

  // Find next upcoming match
  const nextMatch = useMemo(() => {
    if (!markets || markets.length === 0) return null;
    
    const now = new Date();
    const upcomingMatches = markets
      .filter(m => m.gameTime && new Date(m.gameTime) > now)
      .sort((a, b) => new Date(a.gameTime) - new Date(b.gameTime));
    
    return upcomingMatches[0] || null;
  }, [markets]);

  // Simulate latency measurement
  useEffect(() => {
    const measureLatency = () => {
      // Simulate latency between 1-30ms
      setLatency(Math.floor(Math.random() * 30) + 1);
    };
    
    measureLatency();
    const interval = setInterval(measureLatency, 5000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer for next match
  useEffect(() => {
    if (!nextMatch?.gameTime) return;

    const updateCountdown = () => {
      const now = new Date();
      const gameTime = new Date(nextMatch.gameTime);
      const diff = gameTime - now;

      if (diff <= 0) {
        setCountdown({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextMatch]);

  const formatTime = (num) => String(num).padStart(2, '0');

  const matchCount = markets?.length || 0;

  // Extract team names for display
  const getMatchTitle = () => {
    if (!nextMatch) return '';
    const teamA = nextMatch.teamA?.split(' ').pop() || nextMatch.teamA;
    const teamB = nextMatch.teamB?.split(' ').pop() || nextMatch.teamB;
    return `${teamA} vs ${teamB}`;
  };

  return (
    <footer className="h-8 bg-surface/90 border-t border-glass-border flex items-center justify-center px-4 text-xs text-slate-400 gap-2 flex-wrap">
      {/* System Status */}
      <div className="flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-positive/60 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-positive"></span>
        </span>
        <span className="text-positive">{t('footer.connected', '系统正常')}</span>
      </div>

      <span className="text-slate-600">|</span>

      {/* Latency */}
      <div className="flex items-center gap-1">
        <Broadcast size={14} weight="duotone" className="text-accent" />
        <span>{t('footer.latency', '延迟')}: </span>
        <span className="text-white">{latency}ms</span>
      </div>

      <span className="text-slate-600">|</span>

      {/* Match Count */}
      <div className="flex items-center gap-1">
        <ChartBar size={14} weight="duotone" className="text-accent" />
        <span>{t('footer.matches', '比赛')}: </span>
        <span className="text-white">{matchCount}</span>
      </div>

      {nextMatch && (
        <>
          <span className="text-slate-600">|</span>

          {/* Next Match Countdown */}
          <div className="flex items-center gap-1">
            <Timer size={14} weight="duotone" className="text-accent-warm" />
            <span className="text-accent-warm">{getMatchTitle()}</span>
            <span>{t('footer.countdown', '开赛倒计时')}</span>
            <span className="text-white font-mono">
              {formatTime(countdown.hours)}:{formatTime(countdown.minutes)}:{formatTime(countdown.seconds)}
            </span>
          </div>
        </>
      )}

      <span className="text-slate-600">|</span>

      {/* Social Links */}
      <div className="flex items-center gap-3">
        <a
          href="https://t.me/polysportschannel"
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent hover:text-accent-soft transition-colors"
          title={t('footer.joinTelegram', '加入官方 Telegram 群')}
        >
          {t('footer.telegram', '官方TG群')}
        </a>
        <a
          href="https://x.com/polysports_ai"
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-white transition-colors"
          title={t('footer.followX', '关注官方 X')}
        >
          {t('footer.twitter', '官方X')}
        </a>
      </div>
    </footer>
  );
}
